import { useMemo, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { ChartWidgetConfig, ExecutionDataRow, AggregationType, MetricDefinition, MetricFormat } from '@monitoring-panel/types';
import { formatDate, formatNumber } from '@/lib/utils';
import { evaluateFormula, getFormulaDependencies } from '@/lib/formula';
import {
  ADVIA_CHART_OPTS,
  ADVIA_ECHARTS_THEME,
  categoricalSeries,
  solid,
  tooltipRow,
  tooltipTitle,
} from '@/lib/advia-charts';

interface ChartWidgetProps {
  config: ChartWidgetConfig;
  campaignData: ExecutionDataRow[];
  availableMetrics: MetricDefinition[];
  /**
   * Compact mode for small previews (e.g. the wide card list view):
   * hides the legend and axis names, and tightens the grid so the plot fills
   * the space. The tooltip still surfaces every value on hover.
   */
  compact?: boolean;
}

export function ChartWidget({ config, campaignData, availableMetrics, compact = false }: ChartWidgetProps) {
  const { chartType, xAxis, yAxis, aggregation, stacked, seriesConfig, lineColumns } = config;
  // In compact mode the legend is always suppressed regardless of the saved config.
  const showLegend = compact ? false : config.showLegend;

  // Build a map of metric definitions by ID for quick lookup
  const metricsById = useMemo(() => {
    const map = new Map<string, MetricDefinition>();
    for (const m of availableMetrics) {
      map.set(m.id, m);
    }
    return map;
  }, [availableMetrics]);

  // Helper to get display name for a column
  const getColumnDisplayName = useCallback((col: string): string => {
    if (col.startsWith('metric:')) {
      const metricId = col.substring(7);
      const metric = metricsById.get(metricId);
      return metric?.name || metricId;
    }
    return formatColumnName(col);
  }, [metricsById]);

  // Helper to get format for a column
  const getColumnFormat = useCallback((col: string): { format: MetricFormat; decimals: number } => {
    if (col.startsWith('metric:')) {
      const metricId = col.substring(7);
      const metric = metricsById.get(metricId);
      return { format: metric?.format || 'number', decimals: metric?.decimals ?? 2 };
    }
    // Auto-detect format based on column name
    const lowerCol = col.toLowerCase();
    if (lowerCol.includes('cost') || lowerCol.includes('spent') || lowerCol.includes('revenue')) {
      return { format: 'euro', decimals: 2 };
    }
    if (lowerCol.includes('rate') || lowerCol.includes('percentage') || lowerCol.includes('ctr')) {
      return { format: 'percentage', decimals: 2 };
    }
    return { format: 'number', decimals: 0 };
  }, [metricsById]);

  const chartOption: EChartsOption = useMemo(() => {
    if (campaignData.length === 0 || yAxis.length === 0) {
      return {};
    }

    // Separate raw columns from calculated metrics
    const rawColumns: string[] = [];
    const metricColumns: string[] = [];
    for (const col of yAxis) {
      if (col.startsWith('metric:')) {
        metricColumns.push(col);
      } else {
        rawColumns.push(col);
      }
    }

    // Collect all raw column dependencies needed for calculated metrics
    const allDependencies = new Set<string>(rawColumns);
    for (const col of metricColumns) {
      const metricId = col.substring(7);
      const metric = metricsById.get(metricId);
      if (metric) {
        const deps = getFormulaDependencies(metric.formula);
        for (const dep of deps) {
          allDependencies.add(dep);
        }
      }
    }

    // Group raw data by xAxis value - collect all needed columns for aggregation
    const grouped = new Map<string, Record<string, number[]>>();

    for (const row of campaignData) {
      const xValue = String(row[xAxis] || '');
      if (!grouped.has(xValue)) {
        grouped.set(xValue, {});
      }
      const group = grouped.get(xValue)!;

      // Collect values for all dependencies (raw columns needed for calculations)
      for (const col of allDependencies) {
        const value = row[col];
        if (typeof value === 'number') {
          if (!group[col]) group[col] = [];
          group[col].push(value);
        }
      }
    }

    // Sort by xAxis and aggregate
    const sortedKeys = Array.from(grouped.keys()).sort();
    const xAxisData = sortedKeys.map((key) =>
      xAxis === 'date' ? formatDate(key) : key
    );

    // Aggregate raw columns first, then calculate metrics on aggregated values
    const aggregatedData = new Map<string, Map<string, number>>();
    for (const xKey of sortedKeys) {
      const group = grouped.get(xKey) || {};
      const aggregatedGroup = new Map<string, number>();

      // Aggregate all raw dependencies
      for (const col of allDependencies) {
        const values = group[col] || [];
        aggregatedGroup.set(col, aggregate(values, aggregation));
      }

      // Calculate metrics from aggregated values
      for (const col of metricColumns) {
        const metricId = col.substring(7);
        const metric = metricsById.get(metricId);
        if (metric) {
          const context: Record<string, number> = {};
          for (const [key, value] of aggregatedGroup) {
            context[key] = value;
          }
          const result = evaluateFormula(metric.formula, context);
          aggregatedGroup.set(col, result ?? 0);
        }
      }

      aggregatedData.set(xKey, aggregatedGroup);
    }

    // Build a map of series configs by column for quick lookup
    const seriesConfigMap = new Map<string, { seriesType: 'bar' | 'line'; yAxisPosition: 'left' | 'right' }>();
    if (seriesConfig) {
      for (const sc of seriesConfig) {
        seriesConfigMap.set(sc.column, { seriesType: sc.seriesType, yAxisPosition: sc.yAxisPosition });
      }
    }
    // Fallback for legacy lineColumns support
    const lineColumnsSet = new Set(lineColumns || []);

    // Helper to get series config for a column
    const getSeriesConfig = (col: string, index: number) => {
      const config = seriesConfigMap.get(col);
      if (config) return config;
      // Legacy fallback for mixed charts using lineColumns
      if (chartType === 'mixed' && lineColumnsSet.has(col)) {
        return { seriesType: 'line' as const, yAxisPosition: 'right' as const };
      }
      // Default: use chart type, first series on left, rest on right
      return {
        seriesType: (chartType === 'bar' || chartType === 'mixed' ? 'bar' : 'line') as 'bar' | 'line',
        yAxisPosition: (index === 0 ? 'left' : 'right') as 'left' | 'right',
      };
    };

    // Check if we need dual Y-axes (any series on right axis)
    const hasRightAxis = yAxis.some((col, i) => getSeriesConfig(col, i).yAxisPosition === 'right');

    const series: EChartsOption['series'] = yAxis.map((col, index) => {
      const data = sortedKeys.map((key) => {
        return aggregatedData.get(key)?.get(col) ?? 0;
      });

      const baseConfig = {
        name: getColumnDisplayName(col),
        data,
      };

      if (chartType === 'pie') {
        return {
          type: 'pie' as const,
          radius: ['40%', '70%'],
          data: sortedKeys.map((_key, i) => ({
            name: xAxisData[i],
            value: data[i],
          })),
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(41, 41, 41, 0.2)',
            },
          },
        };
      }

      const colSeriesConfig = getSeriesConfig(col, index);
      const yAxisIndex = colSeriesConfig.yAxisPosition === 'right' ? 1 : 0;

      // Mixed chart: use per-series type (bar or line)
      if (chartType === 'mixed') {
        const isLine = colSeriesConfig.seriesType === 'line';
        return {
          ...baseConfig,
          type: isLine ? ('line' as const) : ('bar' as const),
          smooth: isLine,
          yAxisIndex,
          itemStyle: {
            color: seriesColor(index),
          },
        };
      }

      // For bar/line/area charts, use dual Y-axes based on series config
      return {
        ...baseConfig,
        type: chartType === 'area' ? ('line' as const) : (chartType as 'bar' | 'line'),
        smooth: chartType === 'line' || chartType === 'area',
        stack: stacked ? 'total' : undefined,
        areaStyle: chartType === 'area' ? {} : undefined,
        yAxisIndex: hasRightAxis ? yAxisIndex : undefined,
        itemStyle: {
          color: seriesColor(index),
        },
      };
    });

    // Build column to format map for tooltip
    const columnFormats = new Map<string, { format: MetricFormat; decimals: number }>();
    for (const col of yAxis) {
      columnFormats.set(getColumnDisplayName(col), getColumnFormat(col));
    }

    // Custom tooltip formatter function
    const tooltipFormatter = (params: unknown): string => {
      const paramsList = Array.isArray(params) ? params : [params];
      if (paramsList.length === 0) return '';

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const firstParam = paramsList[0] as any;
      let result = tooltipTitle(firstParam.axisValue || firstParam.name);

      for (const param of paramsList) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const p = param as any;
        const formatInfo = columnFormats.get(p.seriesName) || { format: 'number' as MetricFormat, decimals: 2 };
        const formattedValue = formatNumber(p.value, formatInfo.format, formatInfo.decimals);
        result += tooltipRow(p.seriesName, formattedValue, p.marker);
      }
      return result;
    };

    if (chartType === 'pie') {
      return {
        tooltip: {
          trigger: 'item',
          formatter: (params: unknown) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const p = params as any;
            const formatInfo = columnFormats.get(p.seriesName) || { format: 'number' as MetricFormat, decimals: 2 };
            const formattedValue = formatNumber(p.value, formatInfo.format, formatInfo.decimals);
            return (
              tooltipTitle(p.seriesName) +
              tooltipRow(p.name, `${formattedValue} (${formatNumber(p.percent, 'number', 2)}%)`)
            );
          },
        },
        legend: showLegend
          ? {
              orient: 'vertical',
              right: 10,
              top: 'center',
            }
          : undefined,
        color: sortedKeys.map((_key, i) => seriesColor(i)),
        series,
      };
    }

    // Get columns for left and right axes
    const leftAxisColumns = yAxis.filter((col, i) => getSeriesConfig(col, i).yAxisPosition === 'left');
    const rightAxisColumns = yAxis.filter((col, i) => getSeriesConfig(col, i).yAxisPosition === 'right');

    // Get format for left and right axis labels
    const leftFormat = leftAxisColumns.length > 0 ? getColumnFormat(leftAxisColumns[0]) : { format: 'number' as MetricFormat, decimals: 0 };
    const rightFormat = rightAxisColumns.length > 0 ? getColumnFormat(rightAxisColumns[0]) : { format: 'number' as MetricFormat, decimals: 0 };

    // In compact previews, cap the number of Y ticks and shrink the labels so
    // they never overlap; let ECharts hide any that still would.
    const compactYAxis = compact
      ? { splitNumber: 3, axisLabelExtra: { hideOverlap: true } }
      : { splitNumber: undefined, axisLabelExtra: {} };

    // Build Y-axis configuration with formatters
    const yAxisConfig = hasRightAxis
      ? [
          {
            type: 'value' as const,
            // Hide axis names in compact mode — they eat vertical space.
            name: compact ? '' : leftAxisColumns.length > 0 ? getColumnDisplayName(leftAxisColumns[0]) : '',
            position: 'left' as const,
            splitNumber: compactYAxis.splitNumber,
            axisLabel: {
              ...compactYAxis.axisLabelExtra,
              formatter: (value: number) => formatNumber(value, leftFormat.format, leftFormat.decimals),
            },
          },
          {
            type: 'value' as const,
            name: compact ? '' : rightAxisColumns.length > 0 ? getColumnDisplayName(rightAxisColumns[0]) : '',
            position: 'right' as const,
            splitLine: { show: false },
            splitNumber: compactYAxis.splitNumber,
            axisLabel: {
              ...compactYAxis.axisLabelExtra,
              formatter: (value: number) => formatNumber(value, rightFormat.format, rightFormat.decimals),
            },
          },
        ]
      : {
          type: 'value' as const,
          splitNumber: compactYAxis.splitNumber,
          axisLabel: {
            ...compactYAxis.axisLabelExtra,
            formatter: (value: number) => formatNumber(value, leftFormat.format, leftFormat.decimals),
          },
        };

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: chartType === 'bar' ? 'shadow' : 'cross' },
        formatter: tooltipFormatter,
      },
      legend: showLegend
        ? {
            data: yAxis.map(getColumnDisplayName),
            bottom: 0,
          }
        : undefined,
      grid: compact
        ? {
            left: 2,
            right: hasRightAxis ? 8 : 4,
            top: 8,
            bottom: 4,
            containLabel: true,
          }
        : {
            left: '3%',
            right: hasRightAxis ? '8%' : '4%',
            bottom: showLegend ? '15%' : '3%',
            containLabel: true,
          },
      xAxis: {
        type: 'category',
        data: xAxisData,
        axisLabel: {
          rotate: xAxisData.length > 10 ? 45 : 0,
          // In compact previews, thin out x labels so they don't overlap.
          interval: compact && xAxisData.length > 7 ? 'auto' : 0,
          hideOverlap: compact,
        },
      },
      yAxis: yAxisConfig,
      series,
    };
  }, [campaignData, chartType, xAxis, yAxis, aggregation, showLegend, stacked, seriesConfig, lineColumns, metricsById, getColumnDisplayName, getColumnFormat, compact]);

  if (yAxis.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-adv-tertiary text-adv-body-s">
        No metrics selected
      </div>
    );
  }

  if (campaignData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-adv-tertiary text-adv-body-s">
        No data available
      </div>
    );
  }

  return (
    <ReactECharts
      option={chartOption}
      style={{ height: '100%', width: '100%' }}
      theme={ADVIA_ECHARTS_THEME}
      opts={ADVIA_CHART_OPTS}
    />
  );
}

function seriesColor(index: number): string {
  return solid(categoricalSeries(index));
}

function aggregate(values: number[], type: AggregationType): number {
  if (values.length === 0) return 0;

  switch (type) {
    case 'sum':
      return values.reduce((a, b) => a + b, 0);
    case 'avg':
      return values.reduce((a, b) => a + b, 0) / values.length;
    case 'min':
      return Math.min(...values);
    case 'max':
      return Math.max(...values);
    case 'last':
      return values[values.length - 1];
    default:
      return values.reduce((a, b) => a + b, 0);
  }
}

function formatColumnName(col: string): string {
  return col
    .replace(/^campaign/, '')
    .replace(/([A-Z])/g, ' $1')
    .trim();
}
